/**
 * Portal multi-account "pay across my accounts" view (PD-14190). Shown on the
 * Make Payment page when the portal user manages more than one account. Open
 * invoices are grouped by account; each account section has its own payment-
 * method selector that DEFAULTS to the logged-in account's method (picked at
 * the bottom) but can be OVERRIDDEN to that account's own saved method or its
 * own Account Funds. One submit fans out to one payment per account and the
 * result is reported per account.
 */

import { faPlus, faWallet } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PaymentMethodTypeEnum } from '@supply-mover-2-0/core';
import type {
	PaymentDecline,
	PortalCheckoutPaymentMethodDto,
	PortalPayAccountAllocationDto,
	PortalPayAccountResultDto,
	PortalPayAccountsContextDto,
	SupportedPaymentMethodType,
} from '@supply-mover-2-0/shared';
import {
	extractPaymentDecline,
	PaymentDeclineModal,
	Sidebar,
	Tooltip,
	useTranslation,
} from '@supply-mover-2-0/web-ui';
import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { toCustomerDecline } from '../lib/customerDecline';
import { formatCurrency } from '../lib/formatCurrency';
import { PAGE_CONTENT } from '../lib/pageWidth';
import * as portalPaymentService from '../services/portalPaymentService';
import { useLoginModalStore } from '../stores/loginModalStore';
import {
	DEFAULT_HISTORY_RANGE,
	historyRangeOptions,
	resolveHistoryRange,
} from './HistoryFilterBar';
import { PortalAddPaymentMethod } from './PortalAddPaymentMethod';

const ACCOUNT_FUNDS = 'Account Funds';
const DEFAULT_CHOICE = 'default';
const FUNDS_CHOICE = 'funds';

interface LineStateModel {
	checked: boolean;
	amount: string;
}

function round2(value: number): number {
	return Math.round(value * 100) / 100;
}

function parseAmount(raw: string): number {
	const parsed = parseFloat(raw);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function methodLabel(m: PortalCheckoutPaymentMethodDto): string {
	const last = m.lastFour != null ? `****${m.lastFour}` : '';
	const name = m.name != null ? `(${m.name})` : '';
	// PD-15154 — the card's funding type, so a method quoted with no fee says
	// why. A native <option> cannot carry markup, so it rides in the text
	// alongside the surcharge suffix.
	const category =
		m.cardCategoryLabel != null ? `— ${m.cardCategoryLabel}` : '';
	return `${m.subtype ?? m.type} ${last} ${name} ${category}`
		.replace(/\s+/g, ' ')
		.trim();
}

/**
 * Surcharge suffix for a payment option (PD-15011).
 *
 * This page previously disclosed no fee at all, so a customer could pick a card
 * and be charged the gateway surcharge with nothing on screen saying so — the
 * individual-invoice sidebar showed it, this page did not. A native <select>
 * can't carry markup, so the disclosure rides in the option text.
 *
 * The rate comes from the server's already-resolved effective fee (it applies
 * the client override and the PD-14907 state gate), so a zero here genuinely
 * means "nothing will be added" rather than "not configured". Fee-free options
 * are labelled explicitly rather than left blank: silence reads as unknown, not
 * free, and the cheaper option should get visible credit.
 */
function feeSuffix(
	paymentTypes: Array<SupportedPaymentMethodType>,
	method: { type: string; isSurchargeable?: boolean },
	t: (key: string) => string,
): string {
	const type = paymentTypes.find((pt) => pt.paymentMethodType === method.type);
	if (type == null) {
		return '';
	}
	// PD-15173 — this took only the method TYPE, so every card of a fee-bearing
	// type advertised the type's rate. Only a credit card may be surcharged, so
	// the server takes nothing on debit, prepaid or an unclassified card, and
	// labelling those with a fee described a charge the customer never pays.
	// Resolved server-side as `isSurchargeable`, so the rule cannot drift from
	// what the charge actually does.
	if (type.processingFee > 0 && method.isSurchargeable !== false) {
		return type.processingFeeType === '%'
			? ` — ${type.processingFee}% ${t('fee')}`
			: ` — ${formatCurrency(type.processingFee) ?? ''} ${t('fee')}`;
	}
	return ` — ${t('No fee')}`;
}

export const PayAccountsView: React.FC = () => {
	const { t } = useTranslation();
	const { token, isAuthenticated } = usePortalAuth();
	const navigate = useNavigate();
	const openLoginModal = useLoginModalStore((s) => s.open);

	useEffect(() => {
		if (!isAuthenticated) {
			openLoginModal();
		}
	}, [isAuthenticated, openLoginModal]);

	const [context, setContext] = useState<PortalPayAccountsContextDto | null>(
		null,
	);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState<string | null>(null);

	const [lines, setLines] = useState<Record<number, LineStateModel>>({});
	/** Per-account method choice keyed by clientId: 'default' | 'funds' | a
	 *  saved-method id (as a string). */
	const [accountChoice, setAccountChoice] = useState<Record<number, string>>(
		{},
	);
	/** Per-account on-account deposit amount (raw user input) keyed by clientId
	 *  (PD-14286). Money placed on that account's balance, not on an invoice. */
	const [onAccountByClient, setOnAccountByClient] = useState<
		Record<number, string>
	>({});
	const [defaultMethodId, setDefaultMethodId] = useState<number | null>(null);

	// Relative range key (matching the history surfaces) plus the explicit
	// bounds that only apply when the key is 'custom'. The effective
	// from/to are derived below, so the filtering logic stays unchanged.
	const [rangeKey, setRangeKey] = useState(DEFAULT_HISTORY_RANGE);
	const [customFrom, setCustomFrom] = useState('');
	const [customTo, setCustomTo] = useState('');
	const [overdueOnly, setOverdueOnly] = useState(false);
	// PD-15245 — hide accounts that are only listed because Account Funds
	// deposits are possible (no open invoices matching the filters). OFF by
	// default so the deposit-only rows stay reachable.
	const [withInvoicesOnly, setWithInvoicesOnly] = useState(false);
	// PD-14285 — narrow the (potentially long) multi-account list to a single
	// location; null = "All locations" (the default, view-all behaviour).
	const [locationFilter, setLocationFilter] = useState<number | null>(null);
	const selectAllRef = useRef<HTMLInputElement>(null);
	/** Default-method ids seen on the last fetch — a refetch that surfaces a
	 *  new id means the user just added that method (PD-15014). */
	const knownMethodIdsRef = useRef<Set<number> | null>(null);

	const [showAddPayment, setShowAddPayment] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState<string | null>(null);
	// PD-15185 — a gateway decline, shown as a dialog rather than inline.
	const [decline, setDecline] = useState<PaymentDecline | null>(null);
	const [results, setResults] =
		useState<Array<PortalPayAccountResultDto> | null>(null);

	const fetchContext = useCallback(async (): Promise<void> => {
		if (token == null) {
			return;
		}
		setLoading(true);
		setLoadError(null);
		try {
			const res = await portalPaymentService.getPayAccountsContext(token);
			if (res.success) {
				setContext(res.data);
				// PD-15014 — MERGE the per-invoice / per-account state instead of
				// re-initializing it. Adding a payment method mid-flow refetches
				// this context, and a full reset silently wiped every ticked
				// invoice and typed amount the user had staged. Rows for invoices
				// that no longer come back are pruned.
				setLines((prev) => {
					const next: Record<number, LineStateModel> = {};
					for (const acct of res.data.accounts) {
						for (const inv of acct.openInvoices) {
							next[inv.id] = prev[inv.id] ?? { checked: false, amount: '' };
						}
					}
					return next;
				});
				setAccountChoice((prev) => {
					const next: Record<number, string> = {};
					for (const acct of res.data.accounts) {
						next[acct.clientId] = prev[acct.clientId] ?? DEFAULT_CHOICE;
					}
					return next;
				});
				setOnAccountByClient((prev) => {
					const next: Record<number, string> = {};
					for (const acct of res.data.accounts) {
						const kept = prev[acct.clientId] ?? '';
						if (kept !== '') {
							next[acct.clientId] = kept;
						}
					}
					return next;
				});
				const methods = res.data.defaultSavedPaymentMethods;
				// A method that appears on a refetch was just added through the
				// sidebar — select it, since paying with it is why it was added.
				const prevKnown = knownMethodIdsRef.current;
				knownMethodIdsRef.current = new Set(methods.map((m) => m.id));
				const fresh =
					prevKnown == null ? null : methods.find((m) => !prevKnown.has(m.id));
				setDefaultMethodId((prev) => {
					if (fresh != null) {
						return fresh.id;
					}
					if (prev != null && methods.some((m) => m.id === prev)) {
						return prev;
					}
					const primary = methods.find((m) => m.isPrimary);
					return primary?.id ?? (methods.length > 0 ? methods[0].id : null);
				});
			}
		} catch (err) {
			setLoadError(
				err instanceof Error ? err.message : t('Failed to load payment data'),
			);
		} finally {
			setLoading(false);
		}
	}, [token, t]);

	useEffect(() => {
		void fetchContext();
	}, [fetchContext]);

	const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);

	// Effective bounds for the active range. Everything downstream still reads
	// plain ISO dates, so swapping the control changed no filtering logic.
	// 'all' resolves to no bounds, which is why these default to ''.
	const { dateFrom, dateTo } = useMemo(() => {
		const bounds = resolveHistoryRange(rangeKey, customFrom, customTo);
		return { dateFrom: bounds.dateFrom ?? '', dateTo: bounds.dateTo ?? '' };
	}, [rangeKey, customFrom, customTo]);

	// Accounts with their invoices narrowed by the Date/Overdue filters;
	// accounts left with no matching invoices are dropped from the view.
	const filteredAccounts = useMemo(
		() =>
			(context?.accounts ?? [])
				.filter(
					(acct) => locationFilter == null || acct.clientId === locationFilter,
				)
				.map((acct) => {
					const openInvoices = acct.openInvoices.filter((inv) => {
						// PD-15441 — credit rows are separated out below; the
						// payable list must never contain one (PD-15014).
						if (inv.isCreditMemo === true) {
							return false;
						}
						const d = inv.date?.slice(0, 10) ?? null;
						if (dateFrom !== '' && (d == null || d < dateFrom)) {
							return false;
						}
						if (dateTo !== '' && (d == null || d > dateTo)) {
							return false;
						}
						if (overdueOnly) {
							const due = inv.dueDate?.slice(0, 10) ?? null;
							if (due == null || due >= todayIso) {
								return false;
							}
						}
						return true;
					});
					// PD-15441 — the account's credit memos, deliberately EXEMPT
					// from the date/overdue filters (the single-account screen does
					// the same). Those filters narrow which invoices to pay; a
					// credit memo is not something being paid, and hiding it would
					// silently remove the customer's ability to offset the very
					// invoices the filter left on screen. "Overdue" cannot apply to
					// one at all — a credit memo has no due date. They do drop away
					// with the invoices though: with nothing left to offset, an
					// applied credit has no target.
					const creditMemos =
						openInvoices.length > 0
							? acct.openInvoices.filter((inv) => inv.isCreditMemo === true)
							: [];
					return { ...acct, openInvoices, creditMemos };
				})
				// Keep an account when it has invoices to pay, OR when a deposit
				// is possible on it (so an on-account deposit can be made even
				// with no open invoices) — PD-14286. The "Only show accounts
				// with invoices" checkbox (PD-15245) drops the deposit-only
				// rows so the page reads as a pay-invoices worklist.
				//
				// PD-14922 — a deposit needs the funds pool AND the tenant's
				// deposit switch. With deposits off, an invoice-less account has
				// nothing to do here: funds are only ever spent against an
				// invoice, and this account has none.
				.filter(
					(acct) =>
						acct.openInvoices.length > 0 ||
						(acct.accountFunds.enabled &&
							acct.accountFunds.depositAllowed &&
							!withInvoicesOnly),
				)
				// PD-15098 — the signed-in account leads the list. On a page of
				// visually-identical cards a deposit typed into "the" apply row
				// near the submit area landed on whichever account happened to
				// render last; the user's own account being first (and badged
				// below) makes the common case the safe case. Display order
				// only — every input stays keyed by clientId.
				.sort((a, b) => {
					const aIsPayer = a.clientId === context?.payerClientId ? 0 : 1;
					const bIsPayer = b.clientId === context?.payerClientId ? 0 : 1;
					return aIsPayer - bIsPayer;
				}),
		[
			context?.accounts,
			context?.payerClientId,
			dateFrom,
			dateTo,
			overdueOnly,
			withInvoicesOnly,
			todayIso,
			locationFilter,
		],
	);

	const filtersActive =
		rangeKey !== DEFAULT_HISTORY_RANGE ||
		overdueOnly ||
		withInvoicesOnly ||
		locationFilter != null;

	const outstandingById = useMemo(() => {
		const map = new Map<number, number>();
		for (const acct of context?.accounts ?? []) {
			for (const inv of acct.openInvoices) {
				map.set(inv.id, inv.outstandingBalance);
			}
		}
		return map;
	}, [context?.accounts]);

	/**
	 * PD-15441 — which rows are credit memos rather than payable invoices.
	 * Read from the RAW context (not the filtered view) so a cap or an error
	 * message is still correct for a row the current filters have hidden.
	 */
	const creditRowIds = useMemo(() => {
		const ids = new Set<number>();
		for (const acct of context?.accounts ?? []) {
			for (const inv of acct.openInvoices) {
				if (inv.isCreditMemo === true) {
					ids.add(inv.id);
				}
			}
		}
		return ids;
	}, [context?.accounts]);

	const checkedIds = useMemo(() => {
		const ids = new Set<number>();
		for (const [id, line] of Object.entries(lines)) {
			if (line.checked) {
				ids.add(Number(id));
			}
		}
		return ids;
	}, [lines]);

	const visibleIds = useMemo(
		() => filteredAccounts.flatMap((a) => a.openInvoices.map((i) => i.id)),
		[filteredAccounts],
	);
	const allChecked =
		visibleIds.length > 0 && visibleIds.every((id) => checkedIds.has(id));
	const someChecked = visibleIds.some((id) => checkedIds.has(id));

	useEffect(() => {
		if (selectAllRef.current != null) {
			selectAllRef.current.indeterminate = someChecked && !allChecked;
		}
	}, [someChecked, allChecked]);

	// Account funds is a tenant-level capability — every account block carries
	// the same `enabled` flag, so any one of them tells us whether on-account
	// deposits are possible at all (PD-14286).
	const accountFundsEnabled = (context?.accounts ?? []).some(
		(a) => a.accountFunds.enabled,
	);

	// PD-14922 — adding money to an account can be switched off per tenant while
	// SPENDING existing funds keeps working (an ERP may be unable to record an
	// on-account deposit). Tenant-level like `enabled` above, so `every` also
	// makes an empty account list read as allowed — matching the long-standing
	// behavior for a page with nothing on it yet.
	const accountFundsDepositAllowed = (context?.accounts ?? []).every(
		(a) => a.accountFunds.depositAllowed,
	);
	/** Whether an on-account deposit may be entered anywhere on this page. */
	const canDepositToAccount = accountFundsEnabled && accountFundsDepositAllowed;

	/**
	 * PD-15441 — every per-account money figure derived once, keyed by
	 * clientId: what its checked invoices come to, how much of its own credit
	 * is applied, its deposit, and the resulting NET.
	 *
	 * The submit fans out to one payment per account, so netting is
	 * necessarily per account too — a credit memo belongs to one client and
	 * can only offset that client's invoices. Deriving it in one place keeps
	 * the account card, the grand total, the fee preview, the guards and the
	 * request from each computing it slightly differently.
	 */
	const accountTotals = useMemo(() => {
		const map = new Map<
			number,
			{
				invoiceSum: number;
				creditApplied: number;
				deposit: number;
				net: number;
				availableCredit: number;
				creditExceedsInvoices: boolean;
				creditWithDeposit: boolean;
			}
		>();
		for (const acct of filteredAccounts) {
			const invoiceSum = round2(
				acct.openInvoices.reduce(
					(sum, inv) =>
						sum +
						(lines[inv.id].checked ? parseAmount(lines[inv.id].amount) : 0),
					0,
				),
			);
			const creditApplied = round2(
				acct.creditMemos.reduce(
					(sum, memo) =>
						sum +
						(lines[memo.id].checked ? parseAmount(lines[memo.id].amount) : 0),
					0,
				),
			);
			const choice = accountChoice[acct.clientId] ?? DEFAULT_CHOICE;
			const deposit =
				choice === FUNDS_CHOICE
					? 0
					: parseAmount(onAccountByClient[acct.clientId] ?? '');
			map.set(acct.clientId, {
				invoiceSum,
				creditApplied,
				deposit,
				net: round2(invoiceSum - creditApplied + deposit),
				availableCredit: round2(
					acct.creditMemos.reduce(
						(sum, memo) => sum + memo.outstandingBalance,
						0,
					),
				),
				// Applied credit records applications; it is never refunded, so it
				// may not exceed the invoices it offsets. Half-cent tolerance so
				// float noise in the inputs cannot raise a phantom error.
				creditExceedsInvoices: round2(invoiceSum - creditApplied) < -0.004,
				// The server refuses the combination outright: a deposit alongside
				// applied credit blurs what the credit paid for.
				creditWithDeposit: creditApplied > 0 && deposit > 0,
			});
		}
		return map;
	}, [filteredAccounts, lines, accountChoice, onAccountByClient]);

	// Grand total = each visible account's NET (invoices less applied credit,
	// plus any deposit), so the figure matches exactly what the submit will
	// charge across the fan-out.
	const total = useMemo(
		() =>
			round2(
				filteredAccounts.reduce(
					(sum, acct) => sum + (accountTotals.get(acct.clientId)?.net ?? 0),
					0,
				),
			),
		[filteredAccounts, accountTotals],
	);

	/** Total credit applied across every visible account. */
	const creditAppliedTotal = useMemo(
		() =>
			round2(
				filteredAccounts.reduce(
					(sum, acct) =>
						sum + (accountTotals.get(acct.clientId)?.creditApplied ?? 0),
					0,
				),
			),
		[filteredAccounts, accountTotals],
	);

	/** Accounts whose credit selection is invalid — blocks the whole submit. */
	const hasAccountCreditErrors = useMemo(
		() =>
			filteredAccounts.some((acct) => {
				const totals = accountTotals.get(acct.clientId);
				return (
					totals?.creditExceedsInvoices === true ||
					totals?.creditWithDeposit === true
				);
			}),
		[filteredAccounts, accountTotals],
	);

	const lineErrors = useMemo(() => {
		const errors = new Map<number, string>();
		for (const [idStr, line] of Object.entries(lines)) {
			if (!line.checked) {
				continue;
			}
			const outstanding = outstandingById.get(Number(idStr));
			if (
				outstanding != null &&
				parseAmount(line.amount) > outstanding + 0.005
			) {
				// PD-15441 — a credit row's cap is the credit REMAINING on the
				// memo, so it needs its own wording; "outstanding balance" reads
				// as something the customer owes.
				errors.set(
					Number(idStr),
					creditRowIds.has(Number(idStr))
						? t('Exceeds remaining credit')
						: t('Exceeds outstanding balance'),
				);
			}
		}
		return errors;
	}, [lines, outstandingById, creditRowIds, t]);

	// PD-15014 — the processing fees this submit will actually add. The page
	// disclosed the fee RATE in the option labels (PD-15011) but the charged
	// total still silently exceeded the "Pay" figure. One payment is captured
	// per contributing account, so each account's fee is sized on its own
	// amount, with that account's effective (state-gated) rate and the funding
	// card's surchargeability — and the percentage is FLOORED per transaction,
	// matching the server's charge (PD-15174).
	const processingFees = useMemo(() => {
		let fees = 0;
		for (const acct of filteredAccounts) {
			const choice = accountChoice[acct.clientId] ?? DEFAULT_CHOICE;
			if (choice === FUNDS_CHOICE) {
				continue;
			}
			const totals = accountTotals.get(acct.clientId);
			if (totals == null || totals.net <= 0) {
				continue;
			}
			const method =
				choice === DEFAULT_CHOICE
					? (context?.defaultSavedPaymentMethods ?? []).find(
							(m) => m.id === defaultMethodId,
						)
					: acct.savedPaymentMethods.find((m) => String(m.id) === choice);
			if (method == null || !method.isSurchargeable) {
				continue;
			}
			const type = acct.paymentTypes.find(
				(pt) => pt.paymentMethodType === method.type,
			);
			if (type == null || type.processingFee <= 0) {
				continue;
			}
			if (type.processingFeeType !== '%') {
				fees += type.processingFee;
				continue;
			}
			if (totals.creditApplied > 0) {
				// PD-15441 — mirror the server's sizing exactly when credit is
				// applied: each invoice line carries its OWN floored fee, and the
				// applied credit relieves the fee this payment adds (floored, and
				// capped at those new fees so a fee already carried inside an
				// invoice's balance is never clawed back). Flooring a single
				// pooled figure over the net can land a cent under what the card
				// is actually charged, and the disclosed total has to match the
				// charge to the cent.
				const newFees = acct.openInvoices.reduce((sum, inv) => {
					if (!lines[inv.id].checked) {
						return sum;
					}
					return (
						sum +
						Math.floor(parseAmount(lines[inv.id].amount) * type.processingFee) /
							100
					);
				}, 0);
				const relief = Math.min(
					Math.floor(totals.creditApplied * type.processingFee) / 100,
					newFees,
				);
				fees += round2(newFees - relief);
				continue;
			}
			fees += Math.floor(totals.net * type.processingFee) / 100;
		}
		return round2(fees);
	}, [
		filteredAccounts,
		accountTotals,
		lines,
		accountChoice,
		defaultMethodId,
		context?.defaultSavedPaymentMethods,
	]);

	// Does any account that is contributing (a checked invoice OR an on-account
	// deposit) rely on the default method? If so a default method is required.
	const anyUsesDefault = useMemo(
		() =>
			filteredAccounts.some((acct) => {
				if (
					(accountChoice[acct.clientId] ?? DEFAULT_CHOICE) !== DEFAULT_CHOICE
				) {
					return false;
				}
				const hasInvoice = acct.openInvoices.some(
					(inv) => lines[inv.id].checked,
				);
				const hasDeposit =
					parseAmount(onAccountByClient[acct.clientId] ?? '') > 0;
				return hasInvoice || hasDeposit;
			}),
		[filteredAccounts, accountChoice, lines, onAccountByClient],
	);

	const canSubmit =
		!submitting &&
		// PD-15441 — a $0 total is submittable when applied credit fully covers
		// the selection: that payment exists to RECORD the applications, and
		// the server skips the gateway for it. A negative net, or credit with
		// no invoices to offset, stays blocked.
		total >= 0 &&
		(total > 0 || creditAppliedTotal > 0) &&
		!hasAccountCreditErrors &&
		lineErrors.size === 0 &&
		(!anyUsesDefault || defaultMethodId != null);

	// ─── Handlers ────────────────────────────────────────────────────
	const clearResults = (): void => {
		setResults(null);
		setSubmitError(null);
	};

	const toggleLine = (invoiceId: number, outstanding: number): void => {
		setLines((prev) => {
			const current = prev[invoiceId] ?? { checked: false, amount: '' };
			const checked = !current.checked;
			return {
				...prev,
				[invoiceId]: { checked, amount: checked ? outstanding.toFixed(2) : '' },
			};
		});
		clearResults();
	};

	const setLineAmount = (invoiceId: number, amount: string): void => {
		setLines((prev) => ({ ...prev, [invoiceId]: { checked: true, amount } }));
		clearResults();
	};

	/**
	 * PD-15441 — toggling one of THIS account's credit memos. The fill is the
	 * smaller of the memo's remaining credit and what the account's currently
	 * selected invoices can still absorb, so checking a credit can never on
	 * its own push that account's net below zero. Everything is scoped to the
	 * one account: a memo belongs to a single client and the fan-out charges
	 * each client separately, so credit never crosses accounts.
	 */
	const toggleCreditMemo = (
		acct: {
			openInvoices: Array<{ id: number }>;
			creditMemos: Array<{ id: number }>;
		},
		memoId: number,
		remainingCredit: number,
	): void => {
		setLines((prev) => {
			const current = prev[memoId] ?? { checked: false, amount: '' };
			if (current.checked) {
				return { ...prev, [memoId]: { checked: false, amount: '' } };
			}
			const selectedInvoiceSum = acct.openInvoices.reduce((sum, inv) => {
				const line = prev[inv.id] as LineStateModel | undefined;
				return line?.checked === true ? sum + parseAmount(line.amount) : sum;
			}, 0);
			const otherCreditsApplied = acct.creditMemos.reduce((sum, other) => {
				if (other.id === memoId) {
					return sum;
				}
				const line = prev[other.id] as LineStateModel | undefined;
				return line?.checked === true ? sum + parseAmount(line.amount) : sum;
			}, 0);
			const absorbable = Math.max(
				0,
				round2(selectedInvoiceSum - otherCreditsApplied),
			);
			const fill = Math.min(remainingCredit, absorbable);
			return {
				...prev,
				[memoId]: { checked: true, amount: fill > 0 ? fill.toFixed(2) : '' },
			};
		});
		clearResults();
	};

	const blurCurrency = (raw: string): string => {
		const parsed = parseFloat(raw);
		return Number.isFinite(parsed) && parsed > 0 ? parsed.toFixed(2) : '';
	};

	const toggleSelectAll = (): void => {
		const shouldCheck = !allChecked;
		setLines((prev) => {
			const next = { ...prev };
			for (const id of visibleIds) {
				next[id] = shouldCheck
					? { checked: true, amount: (outstandingById.get(id) ?? 0).toFixed(2) }
					: { checked: false, amount: '' };
			}
			return next;
		});
		clearResults();
	};

	// Per-account (per ownership/parent collection) select-all: toggles only
	// that account's currently-visible invoices, independent of the global one.
	const toggleAccountSelectAll = (invoices: Array<{ id: number }>): void => {
		const ids = invoices.map((inv) => inv.id);
		const allOn = ids.length > 0 && ids.every((id) => checkedIds.has(id));
		setLines((prev) => {
			const next = { ...prev };
			for (const id of ids) {
				next[id] = allOn
					? { checked: false, amount: '' }
					: {
							checked: true,
							amount: (outstandingById.get(id) ?? 0).toFixed(2),
						};
			}
			return next;
		});
		clearResults();
	};

	const accountName = (clientId: number): string =>
		(context?.accounts ?? []).find((a) => a.clientId === clientId)
			?.clientName ?? `#${clientId}`;

	const handleSubmit = async (): Promise<void> => {
		if (token == null || !canSubmit || context == null) {
			return;
		}
		setSubmitting(true);
		setSubmitError(null);
		setResults(null);
		try {
			const accounts = filteredAccounts
				.map((acct): PortalPayAccountAllocationDto | null => {
					const invoiceAllocations = [
						...acct.openInvoices
							.filter(
								(inv) =>
									lines[inv.id].checked &&
									parseAmount(lines[inv.id].amount) > 0,
							)
							.map((inv) => ({
								invoiceId: inv.id,
								amount: parseAmount(lines[inv.id].amount),
							})),
						// PD-15441 — this account's credit applications go up
						// NEGATIVE, alongside its invoice lines: the server records
						// one application per document and charges only the net.
						...acct.creditMemos
							.filter(
								(memo) =>
									lines[memo.id].checked &&
									parseAmount(lines[memo.id].amount) > 0,
							)
							.map((memo) => ({
								invoiceId: memo.id,
								amount: -parseAmount(lines[memo.id].amount),
							})),
					];
					const choice = accountChoice[acct.clientId] ?? DEFAULT_CHOICE;
					// Account Funds can't be deposited back onto the account, so an
					// on-account amount is only valid when NOT paying with funds.
					const onAccountAmount =
						choice === FUNDS_CHOICE
							? 0
							: parseAmount(onAccountByClient[acct.clientId] ?? '');
					// Skip accounts that aren't contributing anything (no checked
					// invoices and no deposit) — PD-14286.
					if (invoiceAllocations.length === 0 && onAccountAmount <= 0) {
						return null;
					}
					const group: PortalPayAccountAllocationDto = {
						clientId: acct.clientId,
						invoiceAllocations,
					};
					if (onAccountAmount > 0) {
						group.onAccountAmount = onAccountAmount;
					}
					if (choice === FUNDS_CHOICE) {
						group.overridePaymentMethodType = ACCOUNT_FUNDS;
					} else if (choice !== DEFAULT_CHOICE) {
						group.overridePaymentMethodId = parseInt(choice, 10);
					}
					return group;
				})
				.filter((g): g is PortalPayAccountAllocationDto => g != null);

			const res = await portalPaymentService.payAccounts(token, {
				defaultPaymentMethodId: defaultMethodId ?? undefined,
				accounts,
			});
			setResults(res.results);

			// PD-15185 — the fan-out pays each account separately, so a refusal
			// arrives inside the per-account results rather than as a thrown
			// error. Surface the first one; the results table below carries the
			// rest.
			const declined = res.results.find(
				(result) => result.paymentDecline != null,
			);

			if (declined?.paymentDecline != null) {
				setDecline(declined.paymentDecline);
			}

			if (res.success) {
				window.setTimeout(() => {
					void navigate('/invoices');
				}, 2500);
			}
		} catch (err) {
			const paymentDecline = toCustomerDecline(extractPaymentDecline(err));

			if (paymentDecline != null) {
				setDecline(paymentDecline);
			} else {
				setSubmitError(
					err instanceof Error
						? err.message
						: t('Payment failed. Please try again.'),
				);
			}
		} finally {
			setSubmitting(false);
		}
	};

	// ─── Render ──────────────────────────────────────────────────────
	if (!isAuthenticated) {
		return <div />;
	}

	if (loading) {
		return (
			<div className="flex items-center justify-center py-16">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-portal-accent" />
			</div>
		);
	}

	if (loadError != null) {
		return (
			<div className="p-4">
				<div className="p-4 rounded-lg bg-danger-badge text-danger-tint-fg border border-danger-tint">
					<p className="text-sm text-danger">{t(loadError)}</p>
				</div>
			</div>
		);
	}

	const hasAnyAccounts = (context?.accounts.length ?? 0) > 0;

	return (
		<div className={`px-4 py-4 ${PAGE_CONTENT}`}>
			<h1 className="text-lg md:text-xl font-bold text-default mb-1">
				{t('Make a Payment')}
			</h1>
			<p className="text-sm text-brand-muted mb-4">
				{canDepositToAccount
					? t('Pay open invoices or add funds to your accounts in one place.')
					: t('Pay open invoices across your accounts in one place.')}
			</p>

			{!hasAnyAccounts ? (
				<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
					<p className="text-sm text-brand-muted">
						{t('No open invoices on any of your accounts.')}
					</p>
				</div>
			) : (
				<>
					{/* Filters + bulk select */}
					<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
						{/*
						  Single-row filter header: an unlabeled global select-all
						  (across every account card), the Date range, Overdue only,
						  and Clear filters, all bottom-aligned (feedback 2026-06-17).
						*/}
						<div className="flex flex-wrap items-end gap-3">
							<Tooltip content={t('Select all')} position="top">
								<label className="flex items-center min-h-touch cursor-pointer">
									<input
										ref={selectAllRef}
										type="checkbox"
										aria-label={t('Select all')}
										checked={allChecked}
										disabled={visibleIds.length === 0}
										onChange={toggleSelectAll}
										className="w-4 h-4 text-portal-accent disabled:opacity-50"
									/>
								</label>
							</Tooltip>
							{/*
							  Location filter (PD-14285) — only worth showing when the
							  user actually has more than one account with open invoices;
							  options are the full account list (not the date/overdue-
							  narrowed view) so a location is always selectable.
							*/}
							{(context?.accounts.length ?? 0) > 1 && (
								<div className="min-w-[12rem]">
									<label className="block text-xs font-medium text-brand-muted mb-1">
										{t('Location')}
									</label>
									<select
										value={locationFilter == null ? '' : String(locationFilter)}
										onChange={(e) => {
											const value = e.target.value;
											setLocationFilter(
												value === '' ? null : parseInt(value, 10),
											);
										}}
										aria-label={t('Filter by location')}
										className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-default"
									>
										<option value="">{t('All locations')}</option>
										{(context?.accounts ?? []).map((acct) => (
											<option key={acct.clientId} value={String(acct.clientId)}>
												{t(acct.clientName ?? `#${acct.clientId}`)}
											</option>
										))}
									</select>
								</div>
							)}
							{/*
							  PD-15016 — relative-range picker, matching the history
							  surfaces (Invoices / Orders / Statements / Transactions).
							  This page used to mount a two-month calendar popover, which
							  is both harder to operate for the common case ("the last
							  few months") and rendered a light calendar over the dark
							  brand page. Reusing `historyRangeOptions` keeps one date
							  vocabulary across the storefront; "Custom range" reveals
							  the explicit From/To inputs below for the rarer case.
							*/}
							<div className="min-w-[12rem]">
								<label className="block text-xs font-medium text-brand-muted mb-1">
									{t('Date')}
								</label>
								<select
									value={rangeKey}
									onChange={(e) => {
										setRangeKey(e.target.value);
										clearResults();
									}}
									aria-label={t('Filter by date range')}
									className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-default"
								>
									{historyRangeOptions().map((opt) => (
										<option key={opt.key} value={opt.key}>
											{t(opt.label)}
										</option>
									))}
								</select>
							</div>
							{rangeKey === 'custom' && (
								<>
									<div className="min-w-[9rem]">
										<label className="block text-xs font-medium text-brand-muted mb-1">
											{t('From')}
										</label>
										<input
											type="date"
											value={customFrom}
											onChange={(e) => {
												setCustomFrom(e.target.value);
												clearResults();
											}}
											aria-label={t('From date')}
											className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-default"
										/>
									</div>
									<div className="min-w-[9rem]">
										<label className="block text-xs font-medium text-brand-muted mb-1">
											{t('To')}
										</label>
										<input
											type="date"
											value={customTo}
											onChange={(e) => {
												setCustomTo(e.target.value);
												clearResults();
											}}
											aria-label={t('To date')}
											className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-default"
										/>
									</div>
								</>
							)}
							<label className="flex items-center gap-2 cursor-pointer min-h-touch">
								<input
									type="checkbox"
									checked={overdueOnly}
									onChange={(e) => {
										setOverdueOnly(e.target.checked);
									}}
									className="w-4 h-4 text-portal-accent"
								/>
								<span className="text-sm text-default">
									{t('Overdue only')}
								</span>
							</label>
							{/* PD-15245 — drop the accounts that only appear because an
							    on-account deposit is possible (no matching invoices). */}
							<label className="flex items-center gap-2 cursor-pointer min-h-touch">
								<input
									type="checkbox"
									checked={withInvoicesOnly}
									onChange={(e) => {
										setWithInvoicesOnly(e.target.checked);
									}}
									className="w-4 h-4 text-portal-accent"
								/>
								<span className="text-sm text-default">
									{t('Only show accounts with invoices')}
								</span>
							</label>
							{filtersActive && (
								<button
									type="button"
									onClick={() => {
										setRangeKey(DEFAULT_HISTORY_RANGE);
										setCustomFrom('');
										setCustomTo('');
										setOverdueOnly(false);
										setWithInvoicesOnly(false);
										setLocationFilter(null);
									}}
									className="text-xs font-medium text-portal-accent hover:underline min-h-touch"
								>
									{t('Clear filters')}
								</button>
							)}
						</div>
					</div>

					{filteredAccounts.length === 0 ? (
						<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
							<p className="text-sm text-brand-muted">
								{t('No invoices match your filters.')}
							</p>
						</div>
					) : (
						filteredAccounts.map((acct) => {
							const choice = accountChoice[acct.clientId] ?? DEFAULT_CHOICE;
							const acctIds = acct.openInvoices.map((inv) => inv.id);
							const acctAllChecked =
								acctIds.length > 0 && acctIds.every((id) => checkedIds.has(id));
							const acctSomeChecked = acctIds.some((id) => checkedIds.has(id));
							// What this account is charged: checked invoice amounts,
							// less the credit applied on this account (PD-15441),
							// plus its on-account deposit (PD-14286).
							const totals = accountTotals.get(acct.clientId) ?? {
								invoiceSum: 0,
								creditApplied: 0,
								deposit: 0,
								net: 0,
								availableCredit: 0,
								creditExceedsInvoices: false,
								creditWithDeposit: false,
							};
							const sum = totals.net;
							return (
								<div
									key={acct.clientId}
									className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4"
								>
									{/* Account header + per-account method selector */}
									<div className="flex flex-wrap items-center justify-between gap-3 mb-3 pb-3 border-b border-brand-border">
										<div className="flex items-center gap-3 min-w-0">
											{acctIds.length > 0 && (
												<Tooltip
													content={t('Select all for this account')}
													position="top"
												>
													<input
														type="checkbox"
														aria-label={t('Select all for this account')}
														checked={acctAllChecked}
														ref={(el) => {
															if (el != null) {
																el.indeterminate =
																	acctSomeChecked && !acctAllChecked;
															}
														}}
														onChange={() => {
															toggleAccountSelectAll(acct.openInvoices);
														}}
														className="w-4 h-4 text-portal-accent shrink-0"
													/>
												</Tooltip>
											)}
											<div className="min-w-0">
												<p className="text-sm font-semibold text-default">
													{t(acct.clientName ?? `#${acct.clientId}`)}
													{/* PD-15098: mark the signed-in account so it can't
													    be confused with the other managed accounts. */}
													{acct.clientId === context?.payerClientId && (
														<span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-portal-accent/10 text-portal-accent align-middle">
															{t('Your account')}
														</span>
													)}
												</p>
												{(sum > 0 || totals.creditApplied > 0) && (
													<p className="text-xs text-brand-muted">
														{t('Paying')}: {t(formatCurrency(sum) ?? '')}
													</p>
												)}
												{/* PD-15441 — say what the credit did to this
												    account's figure, on the account it applies to.
												    The grand total below sums the NETS, so without
												    this the number simply looks too low. */}
												{totals.creditApplied > 0 && (
													<p className="text-xs text-portal-accent">
														{t('Includes {{amount}} of applied credit.', {
															amount:
																formatCurrency(totals.creditApplied) ?? '',
														})}
													</p>
												)}
											</div>
										</div>
										<div>
											<label className="block text-xs font-medium text-brand-muted mb-1">
												{t('Pay with')}
											</label>
											<select
												value={choice}
												onChange={(e) => {
													setAccountChoice((prev) => ({
														...prev,
														[acct.clientId]: e.target.value,
													}));
													clearResults();
												}}
												className="px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm max-w-[16rem]"
											>
												<option value={DEFAULT_CHOICE}>
													{t('Default method')}
												</option>
												{acct.savedPaymentMethods.map((m) => (
													<option key={m.id} value={String(m.id)}>
														{t(methodLabel(m))}
														{feeSuffix(
															context?.defaultPaymentTypes ?? [],
															m,
															t,
														)}
													</option>
												))}
												{acct.accountFunds.enabled &&
													acct.accountFunds.available > 0 && (
														<option value={FUNDS_CHOICE}>
															{t('Account Funds')} —{' '}
															{t(
																formatCurrency(acct.accountFunds.available) ??
																	'',
															)}
														</option>
													)}
											</select>
										</div>
									</div>

									{/* Invoices for this account */}
									{acct.openInvoices.length === 0 && (
										<p className="text-sm text-brand-muted">
											{filtersActive
												? t('No invoices match your filters.')
												: t('No open invoices on this account.')}
										</p>
									)}
									<div className="space-y-2">
										{acct.openInvoices.map((invoice) => {
											const line = lines[invoice.id] ?? {
												checked: false,
												amount: '',
											};
											const lineError = lineErrors.get(invoice.id);
											return (
												<div
													key={invoice.id}
													className="flex flex-wrap items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-brand-input"
												>
													<input
														type="checkbox"
														checked={line.checked}
														onChange={() => {
															toggleLine(
																invoice.id,
																invoice.outstandingBalance,
															);
														}}
														className="w-4 h-4 text-portal-accent"
													/>
													<div className="flex-1 min-w-[120px]">
														<p className="text-sm font-medium text-default">
															{t('Invoice')} #{t(invoice.invoiceNumber)}
														</p>
														<p className="text-xs text-brand-muted">
															{t('Outstanding')}:{' '}
															{t(
																formatCurrency(invoice.outstandingBalance) ??
																	'',
															)}
															{invoice.dueDate != null && (
																<>
																	{' · '}
																	{t('Due')} {t(invoice.dueDate)}
																</>
															)}
														</p>
													</div>
													<div className="relative w-32">
														<span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted text-sm">
															$
														</span>
														<input
															type="number"
															value={line.amount}
															disabled={!line.checked}
															min="0.01"
															step="0.01"
															onChange={(e) => {
																setLineAmount(invoice.id, e.target.value);
															}}
															onBlur={() => {
																setLineAmount(
																	invoice.id,
																	blurCurrency(line.amount),
																);
															}}
															placeholder="0.00"
															className="w-full pl-7 pr-2 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-right text-default disabled:opacity-50"
														/>
													</div>
													{lineError != null && (
														<p className="w-full text-xs text-danger text-right">
															{t(lineError)}
														</p>
													)}
												</div>
											);
										})}
									</div>

									{/* PD-15441 — this account's credit memos (RMAs). Applied
									    credit reduces what THIS account is charged and cannot
									    reach another account's invoices: the submit captures one
									    payment per account, and a memo belongs to one client.
									    Rows only arrive when the tenant has
									    payments.pay_invoices_with_credit_memos enabled. */}
									{acct.creditMemos.length > 0 && (
										<div className="mt-3 border-t border-brand-border pt-3">
											<div className="flex items-center justify-between mb-1">
												<h3 className="text-sm font-semibold text-default">
													{t('Credit Memos')}
												</h3>
												<p className="text-xs text-brand-muted">
													{t('Available credit')}:{' '}
													{t(formatCurrency(totals.availableCredit) ?? '')}
												</p>
											</div>
											<p className="text-xs text-brand-muted mb-2">
												{t(
													'Applied credit reduces the amount charged for this account’s invoices above.',
												)}
											</p>
											<div className="space-y-2">
												{acct.creditMemos.map((memo) => {
													const line = lines[memo.id] ?? {
														checked: false,
														amount: '',
													};
													const memoError = lineErrors.get(memo.id);
													return (
														<div
															key={memo.id}
															className="flex flex-wrap items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-brand-input"
														>
															<input
																type="checkbox"
																checked={line.checked}
																onChange={() => {
																	toggleCreditMemo(
																		acct,
																		memo.id,
																		memo.outstandingBalance,
																	);
																}}
																className="w-4 h-4 text-portal-accent"
															/>
															<div className="flex-1 min-w-[120px]">
																<p className="text-sm font-medium text-default">
																	{t('Credit Memo')} #{t(memo.invoiceNumber)}
																</p>
																<p className="text-xs text-brand-muted">
																	{t('Remaining credit')}:{' '}
																	{t(
																		formatCurrency(memo.outstandingBalance) ??
																			'',
																	)}
																	{memo.date != null && (
																		<>
																			{' · '}
																			{t('Dated')} {t(memo.date)}
																		</>
																	)}
																</p>
															</div>
															{/* The minus sign is the whole visual difference
															    between paying a document and applying one, so
															    it replaces the dollar prefix rather than
															    sitting beside it. */}
															<div className="relative w-32">
																<span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted text-sm">
																	-$
																</span>
																<input
																	type="number"
																	value={line.amount}
																	disabled={!line.checked}
																	min="0.01"
																	step="0.01"
																	onChange={(e) => {
																		setLineAmount(memo.id, e.target.value);
																	}}
																	onBlur={() => {
																		setLineAmount(
																			memo.id,
																			blurCurrency(line.amount),
																		);
																	}}
																	placeholder="0.00"
																	className="w-full pl-8 pr-2 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-right text-default disabled:opacity-50"
																/>
															</div>
															{memoError != null && (
																<p className="w-full text-xs text-danger text-right">
																	{t(memoError)}
																</p>
															)}
														</div>
													);
												})}
											</div>
											{/* Both guards are also enforced server-side; stating
											    them on the account they belong to is what makes a
											    page of near-identical cards actionable. */}
											{totals.creditExceedsInvoices && (
												<p className="mt-2 text-sm text-danger">
													{t(
														'Applied credit exceeds the selected invoice amounts for this account. Reduce the credit applied or select more invoices.',
													)}
												</p>
											)}
											{totals.creditWithDeposit && (
												<p className="mt-2 text-sm text-danger">
													{t(
														'Credit memos cannot be combined with an Apply to amount on the same account.',
													)}
												</p>
											)}
										</div>
									)}

									{/*
									  On-account deposit for THIS account (PD-14286). Charged to
									  the account's resolved funding method and placed on its
									  balance. Disabled when paying with Account Funds — funds
									  can't be deposited back onto the account. Hidden entirely
									  when the tenant does not allow deposits (PD-14922);
									  spending this account's funds is unaffected.
									*/}
									{canDepositToAccount && (
										<div className="flex flex-wrap items-center gap-3 p-2 -mx-2 mt-3 border-t border-brand-border pt-3">
											<div className="flex-1 min-w-[120px]">
												{/* PD-15098: name the target account in the row itself —
												    scrolled mid-card, "this account" gave no clue whose
												    card the deposit was landing on, and a deposit went
												    to the wrong account that way. */}
												<p className="text-sm font-medium text-default">
													{t('Apply to')}{' '}
													{t(acct.clientName ?? `#${acct.clientId}`)}
												</p>
												<p className="text-xs text-brand-muted">
													{choice === FUNDS_CHOICE
														? t('Not available when paying with Account Funds.')
														: t(
																'Funds added to this account’s balance instead of an invoice.',
															)}
												</p>
											</div>
											<div className="relative w-32">
												<span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted text-sm">
													$
												</span>
												<input
													type="number"
													value={onAccountByClient[acct.clientId] ?? ''}
													min="0"
													step="0.01"
													disabled={choice === FUNDS_CHOICE}
													onChange={(e) => {
														const value = e.target.value;
														setOnAccountByClient((prev) => ({
															...prev,
															[acct.clientId]: value,
														}));
														clearResults();
													}}
													onBlur={() => {
														setOnAccountByClient((prev) => ({
															...prev,
															[acct.clientId]: blurCurrency(
																prev[acct.clientId] ?? '',
															),
														}));
													}}
													placeholder="0.00"
													className="w-full pl-7 pr-2 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-right text-default disabled:opacity-50"
												/>
											</div>
										</div>
									)}
								</div>
							);
						})
					)}

					{/* Default payment method (used by accounts set to "Default method") */}
					<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
						<div className="flex items-center gap-2 mb-2">
							<FontAwesomeIcon icon={faWallet} className="text-portal-accent" />
							<h2 className="text-sm font-semibold text-default">
								{t('Default payment method')}
							</h2>
						</div>
						<p className="text-xs text-brand-muted mb-3">
							{t(
								'Used for any account set to "Default method" above. Stored on your signed-in account.',
							)}
						</p>
						{(context?.defaultSavedPaymentMethods.length ?? 0) > 0 ? (
							<select
								value={defaultMethodId ?? ''}
								onChange={(e) => {
									setDefaultMethodId(parseInt(e.target.value, 10));
									clearResults();
								}}
								className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-default"
							>
								{context?.defaultSavedPaymentMethods.map((m) => (
									<option key={m.id} value={m.id}>
										{t(methodLabel(m))}
										{feeSuffix(context.defaultPaymentTypes, m, t)}
									</option>
								))}
							</select>
						) : (
							<p className="text-xs text-brand-muted">
								{t('No saved payment methods on your account yet.')}
							</p>
						)}
						<button
							type="button"
							onClick={() => {
								setShowAddPayment(true);
							}}
							className="flex items-center gap-1.5 text-xs font-medium text-portal-accent hover:underline mt-2"
						>
							<FontAwesomeIcon icon={faPlus} />
							<span>{t('Add new payment method')}</span>
						</button>
					</div>

					{/* Total + per-account results */}
					<div className="bg-brand-surface border border-brand-border rounded-xl p-4 mb-4">
						<div className="flex items-center justify-between">
							<span className="text-sm font-medium text-strong">
								{t('Total Payment')}
							</span>
							<span className="text-base font-bold text-default">
								{t(formatCurrency(total) ?? '')}
							</span>
						</div>
						{/* PD-15441 — the total is the sum of each account's NET, so
						    the applied credit has to be named or the figure reads as
						    unexplained. */}
						{creditAppliedTotal > 0 && (
							<p className="text-xs text-portal-accent mt-1">
								{t('Includes {{amount}} of applied credit.', {
									amount: formatCurrency(creditAppliedTotal) ?? '',
								})}
							</p>
						)}
						{/* PD-15014 — say what the card will actually be charged.
						    Mirrors the individual-invoice sidebar's fee preview. */}
						{processingFees > 0 && (
							<>
								<div className="flex items-center justify-between mt-2">
									<span className="text-sm text-brand-muted">
										{t('Processing fees')}
									</span>
									<span className="text-sm text-default">
										{t(formatCurrency(processingFees) ?? '')}
									</span>
								</div>
								<div className="flex items-center justify-between mt-2 pt-2 border-t border-brand-border">
									<span className="text-sm font-semibold text-default">
										{t('Total charge')}
									</span>
									<span className="text-base font-bold text-default">
										{t(formatCurrency(round2(total + processingFees)) ?? '')}
									</span>
								</div>
							</>
						)}
						{results != null && (
							<div className="mt-3 border-t border-brand-border pt-3 space-y-1">
								{results.map((r) => (
									<div
										key={r.clientId}
										className="flex items-center justify-between text-sm"
									>
										<span className="text-default">
											{t(accountName(r.clientId))}
										</span>
										{r.success ? (
											<span className="text-xs font-medium px-2 py-0.5 rounded-full bg-success-badge text-success-tint-fg border border-success-tint">
												{t(r.transactionStatus ?? 'Paid')}
											</span>
										) : (
											<span className="text-xs text-danger text-right ml-3">
												{t(r.error ?? 'Failed')}
											</span>
										)}
									</div>
								))}
							</div>
						)}
					</div>

					{submitError != null && (
						<div className="mb-4 p-3 rounded-lg bg-danger-badge text-danger-tint-fg border border-danger-tint">
							<p className="text-sm text-danger">{t(submitError)}</p>
						</div>
					)}

					{/* PD-15245 — disabled state matches the support page's Send Reply
					    button (accent fill faded via opacity) instead of the CRM
					    `control-strong` token, which read as an unthemed gray block
					    with white text on brand-tinted dark palettes. `enabled:` on
					    the hover fill keeps the disabled button inert under the
					    pointer; the not-allowed cursor still signals why. */}
					<button
						type="button"
						onClick={() => void handleSubmit()}
						disabled={!canSubmit}
						className="w-full md:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-portal-accent enabled:hover:bg-portal-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors min-h-touch mb-6"
					>
						{submitting ? (
							<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
						) : null}
						{submitting
							? t('Processing...')
							: total <= 0 && creditAppliedTotal > 0
								? // PD-15441 — nothing is being charged, so "Pay $0.00"
									// misdescribes the button. The action is recording the
									// credit applications against the invoices.
									t('Apply Credit')
								: t('Pay {{amount}}', {
										amount: formatCurrency(total > 0 ? total : 0) ?? '',
									})}
					</button>
				</>
			)}

			<Sidebar
				panelClassName="bg-brand-surface"
				isOpen={showAddPayment}
				onClose={() => {
					setShowAddPayment(false);
				}}
				title={t('Add Payment Method')}
				width="md"
				position="right"
				modal={true}
			>
				<div className="p-4">
					<PortalAddPaymentMethod
						onSuccess={() => {
							setShowAddPayment(false);
							void fetchContext();
						}}
						onCancel={() => {
							setShowAddPayment(false);
						}}
						supportedPaymentMethods={(
							context?.defaultPaymentTypes ?? []
						).filter((pt) => pt.paymentMethodType !== ACCOUNT_FUNDS)}
						preSelectedPaymentType={
							(context?.defaultPaymentTypes ?? []).some(
								(pt) => pt.paymentMethodType === 'ACH',
							)
								? PaymentMethodTypeEnum.ACH
								: PaymentMethodTypeEnum.CREDIT_CARD
						}
					/>
				</div>
			</Sidebar>

			{/* PD-15185 — a refused payment, acknowledged deliberately. */}
			<PaymentDeclineModal
				decline={decline}
				onClose={(): void => {
					setDecline(null);
				}}
			/>
		</div>
	);
};

export default PayAccountsView;
